import { useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Button,
} from "@salestrak/ui";

interface DeleteNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isParent: boolean;
  replyCount: number;
  onConfirm: () => Promise<void>;
}

/**
 * Delete confirmation for a note or a whole thread. One AlertDialog at every
 * breakpoint — bottom-stick covers the mobile presentation.
 */
export function DeleteNoteDialog({
  open,
  onOpenChange,
  isParent,
  replyCount,
  onConfirm,
}: DeleteNoteDialogProps) {
  const [deleting, setDeleting] = useState(false);

  const title = isParent ? "Delete thread" : "Delete comment";
  const description =
    isParent && replyCount > 0
      ? `This thread contains ${replyCount} ${replyCount === 1 ? "comment" : "comments"} that will be permanently deleted.`
      : "Are you sure you want to delete this comment?";

  async function handleConfirm() {
    setDeleting(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to delete note:", error);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogClose
            render={<Button variant="outline" />}
            disabled={deleting}
          >
            Cancel
          </AlertDialogClose>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            loading={deleting}
          >
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
